<script setup lang="ts">
import { ref } from 'vue';
import { storeOrdersApiClient } from '@api-client/store_orders';
import { toast } from '@/ui-plus/sonner';
import { ExternalLink, XCircle, FileJson, X } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import UberDirectShowDeliveryDialog from './UberDirectShowDeliveryDialog.vue';
import type { ResType } from '@api-client/typeUtils';
type OrderDetailsType = ResType<typeof storeOrdersApiClient[":storeId"]['get_by_order_ids']['$post']>["data"][number];


const props = defineProps<{
    order: OrderDetailsType;
}>();

const viewingDeliveryId = ref<string | null>(null);


// Locally track deliveries cancelled during this session so the UI reflects
// the new state without requiring a parent refetch. Merged with server state
// when rendering.
const locallyCancelledIds = ref<Set<string>>(new Set());
const cancellingIds = ref<Set<string>>(new Set());

const isDeliveryCancelled = (deliveryId: string, isCancelled: boolean) => {
    return isCancelled || locallyCancelledIds.value.has(deliveryId);
};

const cancelUberDirectDelivery = async (deliveryId: string) => {
    if (!confirm(`Cancel Uber Direct delivery?\nReason: out_of_items`)) {
        return;
    }
    cancellingIds.value.add(deliveryId);
    try {
        const { error } = await (await storeOrdersApiClient.uber_direct_cancel_delivery[":storeId"].$post({
            param: { storeId: props.order.storeId },
            json: {
                delivery_id: deliveryId,
                cancelation_reason: "out_of_items",
                additional_description: "cancelled from order page",
            },
        })).json();

        if (error) {
            toast.error(error.message ?? 'Failed to cancel Uber Direct delivery');
            return;
        }
        locallyCancelledIds.value.add(deliveryId);
        toast.success('Uber Direct delivery cancelled');
    } finally {
        cancellingIds.value.delete(deliveryId);
    }
};

</script>
<template>
    <div v-if="order.uberDirectDeliveries && order.uberDirectDeliveries.length > 0"
         class="mt-3 space-y-2">
        <div class="text-sm font-medium">Uber Direct Deliveries</div>
        <div v-for="delivery in order.uberDirectDeliveries"
             :key="delivery.id"
             class="border rounded-md p-2 text-sm flex flex-col gap-1">
            <div class="flex items-center justify-between gap-2">
                <a v-if="delivery.trackingUrl"
                   :href="delivery.trackingUrl"
                   target="_blank"
                   rel="noopener noreferrer"
                   class="inline-flex items-center gap-1 text-xs text-primary underline">
                    <ExternalLink class="h-3 w-3" />
                    Track
                </a>
                <span v-else> </span>

                <span class="text-xs text-muted-foreground font-mono truncate"
                      :title="delivery.uberDeliveryId">
                    {{ delivery.uuid.slice(-5).toUpperCase() }}
                </span>
            </div>

            <div v-if="delivery.cancelReason"
                 class="text-xs text-muted-foreground">
                Reason: {{ delivery.cancelReason }}
            </div>

            <div class="flex items-center gap-2 flex-wrap">
                <Button variant="ghost"
                        size="sm"
                        class="h-7 px-2 text-xs"
                        @click="viewingDeliveryId = delivery.uberDeliveryId">
                    <FileJson class="h-3 w-3 mr-1" />
                    View JSON
                </Button>
                <Button v-if="!isDeliveryCancelled(delivery.uberDeliveryId, delivery.isCancelled)"
                        variant="ghost"
                        size="sm"
                        class="h-7 px-2 text-xs text-destructive hover:text-destructive"
                        :disabled="cancellingIds.has(delivery.uberDeliveryId)"
                        @click="cancelUberDirectDelivery(delivery.uberDeliveryId)">
                    <XCircle class="h-3 w-3 mr-1" />
                    {{ cancellingIds.has(delivery.uberDeliveryId) ? 'Cancelling...' : 'Cancel' }}
                </Button>
                <span v-else
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-destructive/10 text-destructive border-destructive/30">
                    <X class="h-3 w-3" />
                    Cancelled
                </span>
            </div>
        </div>

        <UberDirectShowDeliveryDialog v-if="viewingDeliveryId"
                                      :storeId="order.storeId"
                                      :deliveryId="viewingDeliveryId"
                                      @close="viewingDeliveryId = null" />
    </div>
</template>